/**
 * Clinic Analytics Service
 *
 * Fetches visit data from Firestore and provides pure-function
 * aggregation for the Clinic Health Dashboard.
 */

import { db } from './firebase';

// ============================================================================
// FIRESTORE QUERIES
// ============================================================================

/**
 * Fetch visits for a clinic within a date range.
 * Excludes cancelled visits.
 * @param {string} clinicId
 * @param {Date} startDate - inclusive lower bound
 * @param {Date} endDate   - inclusive upper bound
 * @returns {Promise<Array>} visit objects with JS Date timestamps
 */
export async function getVisitsForRange(clinicId, startDate, endDate) {
  if (!clinicId) return [];

  try {
    const snapshot = await db
      .collection('clinics')
      .doc(clinicId)
      .collection('visits')
      .where('checkedInAt', '>=', startDate)
      .where('checkedInAt', '<=', endDate)
      .orderBy('checkedInAt', 'desc')
      .get();

    return snapshot.docs
      .map((doc) => {
        const d = doc.data();
        return /** @type {Record<string, any> & { id: string }} */ ({
          id: doc.id,
          ...d,
          checkedInAt: d.checkedInAt?.toDate?.() || d.checkedInAt,
          calledAt: d.calledAt?.toDate?.() || d.calledAt,
          completedAt: d.completedAt?.toDate?.() || d.completedAt,
          inProgressAt: d.inProgressAt?.toDate?.() || d.inProgressAt,
        });
      })
      .filter((v) => v.status !== 'cancelled');
  } catch (error) {
    console.error('[ClinicAnalyticsService] getVisitsForRange:', error);
    return [];
  }
}

// ============================================================================
// PURE AGGREGATION
// ============================================================================

function toDateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function diffMinutes(a, b) {
  if (!a || !b) return null;
  return (b - a) / 60000;
}

function buildEmptyDayMap(rangeStart, rangeEnd) {
  const map = {};
  const d = new Date(rangeStart);
  d.setHours(0, 0, 0, 0);
  const end = new Date(rangeEnd);
  end.setHours(23, 59, 59, 999);
  while (d <= end) {
    map[toDateKey(d)] = [];
    d.setDate(d.getDate() + 1);
  }
  return map;
}

/**
 * Aggregate raw visit objects into dashboard metrics.
 *
 * @param {Array}  visits     - visit objects with JS Date timestamps
 * @param {Date}   rangeStart - first day of the range (inclusive)
 * @param {Date}   rangeEnd   - last day of the range (inclusive)
 * @returns {{ todayStats: Object, trendData: Object }}
 */
export function aggregateVisitMetrics(visits, rangeStart, rangeEnd) {
  const todayKey = toDateKey(new Date());
  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterdayKey = toDateKey(yesterdayDate);

  // Group visits by day
  const dayMap = buildEmptyDayMap(rangeStart, rangeEnd);
  for (const v of visits) {
    const key = v.checkedInAt instanceof Date ? toDateKey(v.checkedInAt) : null;
    if (key && dayMap[key]) {
      dayMap[key].push(v);
    }
  }

  // --- Today's snapshot ---------------------------------------------------
  const todayVisits = dayMap[todayKey] || [];
  const yesterdayVisits = dayMap[yesterdayKey] || [];

  const todayStats = buildDayStats(todayVisits, yesterdayVisits);

  // --- Trend data (for charts) -------------------------------------------
  const sortedKeys = Object.keys(dayMap).sort();

  const dailyVolume = sortedKeys.map(key => {
    const dv = dayMap[key];
    return {
      date: key,
      total: dv.length,
      qr: dv.filter(v => v.checkInMethod === 'qr_code').length,
      sms: dv.filter(v => v.checkInMethod === 'sms').length,
    };
  });

  const dailyWaitTime = sortedKeys.map(key => {
    const waits = dayMap[key]
      .map(v => diffMinutes(v.checkedInAt, v.calledAt))
      .filter(w => w !== null && w >= 0);
    return {
      date: key,
      avgWait: waits.length ? Math.round(waits.reduce((a, b) => a + b, 0) / waits.length) : null,
    };
  });

  // Peak hours — aggregate across entire range
  const hourBuckets = Array.from({ length: 24 }, (_, i) => ({ hour: i, count: 0 }));
  for (const v of visits) {
    if (v.checkedInAt instanceof Date) {
      hourBuckets[v.checkedInAt.getHours()].count += 1;
    }
  }

  // New vs returning patients
  const seenPatients = new Set();
  const patientMix = sortedKeys.map(key => {
    let newCount = 0;
    let returningCount = 0;
    for (const v of dayMap[key]) {
      if (!v.patientId) continue; // SMS check-ins have no patientId
      if (seenPatients.has(v.patientId)) {
        returningCount += 1;
      } else {
        seenPatients.add(v.patientId);
        newCount += 1;
      }
    }
    return { date: key, new: newCount, returning: returningCount };
  });

  return {
    todayStats,
    trendData: {
      dailyVolume,
      dailyWaitTime,
      peakHours: hourBuckets,
      patientMix,
    },
  };
}

function buildDayStats(todayVisits, yesterdayVisits) {
  const total = todayVisits.length;
  const qr = todayVisits.filter(v => v.checkInMethod === 'qr_code').length;
  const sms = todayVisits.filter(v => v.checkInMethod === 'sms').length;

  const waiting = todayVisits.filter(v => v.status === 'checked_in').length;
  const called = todayVisits.filter(v => v.status === 'called' || v.status === 'in_progress').length;
  const completed = todayVisits.filter(v => v.status === 'completed').length;

  const waits = todayVisits
    .map(v => diffMinutes(v.checkedInAt, v.calledAt))
    .filter(w => w !== null && w >= 0);
  const avgWait = waits.length
    ? Math.round(waits.reduce((a, b) => a + b, 0) / waits.length)
    : null;

  const yesterdayTotal = yesterdayVisits.length;
  const volumeDelta = yesterdayTotal > 0
    ? Math.round(((total - yesterdayTotal) / yesterdayTotal) * 100)
    : null;

  return {
    total,
    qr,
    sms,
    waiting,
    called,
    completed,
    avgWait,
    volumeDelta,
  };
}
